import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import { Caption } from '@/components/atoms/Eyebrow';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ActionIcon, type FishGroupActionKind } from '@/components/organisms/FishGroupForms';
import type { TimelineFishGroup } from '@/domain/fishGroups';
import { formatDateShortLocalized } from '@/utils/date/localized';

import {
    canRegisterEvents,
    currentStay,
    groupStartDate,
    latestStay,
    stayBioscopeIds,
} from '../../helpers/siteActions/groupFacts';
import type { TransferFact } from '../../helpers/transferFacts';
import { rowWeightLabel } from '../../helpers/weightText';
import Glyph from '../Glyph/Glyph';

import DetailTransfers from './DetailTransfers';
import StockingMembership, { type StockingActionKind } from './StockingMembership';

import styles from './FishGroupDetailModal.module.scss';

/** The actions a customer registers from the dialog; harvest has no flow yet. */
export type SiteActionKind = 'move' | 'delousing' | 'split' | 'grading' | 'merge';

const ACTIONS: SiteActionKind[] = ['move', 'delousing', 'split', 'grading', 'merge'];

interface FishGroupDetailModalProps {
    /** As the board shows it: masked in demo mode. */
    group: TimelineFishGroup;
    /** The stocking's display name, or null for a group in none. */
    cycleName: string | null;
    locationName: string;
    /** The board's "today". */
    now: Date;
    /** The pen dashboard of the group's current or last pen. */
    dashboardPath: string | null;
    writesDisabled: boolean;
    /** Fish that came into the group and left it through operations at this site. */
    transfers?: { incoming: TransferFact[]; outgoing: TransferFact[] };
    onAction: (kind: SiteActionKind) => void;
    /** Add to stocking or Remove from stocking; without it the dialog only names the stocking. */
    onStockingAction?: (kind: StockingActionKind) => void;
    onClose: () => void;
}

type GroupState = 'active' | 'planned' | 'completed';

function groupState(group: TimelineFishGroup, now: Date): GroupState {
    if (!group.isLive) return 'completed';
    return canRegisterEvents(group, now) ? 'active' : 'planned';
}

const NO_TRANSFERS = { incoming: [], outgoing: [] };

const STATE_TONE = { active: 'success', planned: 'info', completed: 'neutral' } as const;

export default function FishGroupDetailModal(props: FishGroupDetailModalProps) {
    const { group, cycleName, locationName, now, dashboardPath, writesDisabled } = props;
    const { onAction, onStockingAction, onClose, transfers = NO_TRANSFERS } = props;
    const { t, i18n } = useTranslation('fishGroups');
    const locale = i18n.language;
    const registerId = useId();
    const demoNoteId = useId();
    const stockingNoteId = useId();
    const harvestNoteId = useId();

    const state = groupState(group, now);
    const open = currentStay(group);
    const stay = open ?? latestStay(group);
    const bioscopes = stayBioscopeIds(stay);
    const start = groupStartDate(group);
    const weight = rowWeightLabel(group.weight, null, locale, t);
    const placeholder = '–';

    const facts = [
        {
            key: 'pen',
            label: t(open ? 'siteActions.detail.currentlyIn' : 'siteActions.detail.lastIn'),
            value: stay?.cageName ?? placeholder,
        },
        {
            key: 'bioscope',
            label: t(open ? 'siteActions.detail.measuringNow' : 'siteActions.detail.measuredBy'),
            value:
                bioscopes.length > 0
                    ? t('siteActions.bioscopeIds', { ids: bioscopes.join(', ') })
                    : t('board.row.noBioscope'),
        },
        { key: 'species', label: t('siteActions.detail.species'), value: group.fishType.name },
        {
            key: 'weight',
            label: t('siteActions.detail.averageWeight'),
            value: weight.text,
            title: weight.title,
        },
        {
            key: 'start',
            label: t('siteActions.detail.stockingDate'),
            value: start ? formatDateShortLocalized(start, locale) : placeholder,
        },
    ];

    const title = (
        <span className={styles.heading}>
            <ActionIcon kind="fishGroup" size="md" />
            <span className={styles.headingText}>
                <span className={styles.nameLine}>
                    <span className={styles.name}>{group.name}</span>
                    <Badge tone={STATE_TONE[state]}>{t(`siteActions.detail.state.${state}`)}</Badge>
                </span>
                <span className={styles.subtitle}>
                    {t('siteActions.detail.subtitle', {
                        cycle: cycleName ?? t('siteActions.noStocking'),
                        site: locationName,
                    })}
                </span>
            </span>
        </span>
    );

    // A split of a group in no stocking would make groups in none (Mauro, 2026-10-20).
    const needsStocking = (kind: SiteActionKind) =>
        group.stockingCycleId === null && (kind === 'split' || kind === 'grading');
    const unavailableReason = (kind: FishGroupActionKind) => {
        if (kind === 'harvest') return t('siteActions.detail.harvestUnavailable');
        if (writesDisabled) return t('board.actions.notInDemoMode');
        if (kind !== 'fishGroup' && needsStocking(kind))
            return t('siteActions.detail.needsStocking');
        return undefined;
    };
    // A disabled button takes no focus, so its tooltip never reaches keyboard or screen-reader
    // users: each reason is also a visible note the button points at.
    const showsStockingNote = !writesDisabled && group.stockingCycleId === null;
    const describedBy = (kind: FishGroupActionKind) => {
        if (kind === 'harvest') return harvestNoteId;
        if (writesDisabled) return demoNoteId;
        if (kind !== 'fishGroup' && needsStocking(kind)) return stockingNoteId;
        return undefined;
    };

    const actionButton = (kind: FishGroupActionKind, label: string, onClick?: () => void) => (
        <Button
            key={kind}
            variant="secondary"
            size="large"
            fullWidth
            className={styles.action}
            icon={<ActionIcon kind={kind} />}
            disabled={!onClick}
            tooltip={unavailableReason(kind)}
            aria-describedby={describedBy(kind)}
            onClick={onClick}>
            {label}
        </Button>
    );

    const body = (
        <div className={styles.body}>
            <Card as="section" variant="subtle" padding="sm" radius="sm">
                <dl className={styles.facts}>
                    {facts.map((fact) => (
                        <div key={fact.key} className={styles.fact}>
                            <Caption as="dt">{fact.label}</Caption>
                            <dd className={styles.factValue} title={fact.title}>
                                {fact.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </Card>

            <StockingMembership
                cycleName={cycleName}
                writesDisabled={writesDisabled}
                onAction={onStockingAction}
            />

            <DetailTransfers incoming={transfers.incoming} outgoing={transfers.outgoing} />

            <section className={styles.register} aria-labelledby={registerId}>
                <h3 id={registerId} className={styles.registerTitle}>
                    {t('siteActions.detail.registerEvent')}
                </h3>
                {state === 'completed' && (
                    <Alert tone="neutral" size="small">
                        {t('siteActions.detail.completedNote')}
                    </Alert>
                )}
                {state === 'planned' && (
                    <Alert tone="info" size="small">
                        {t('siteActions.detail.plannedNote', {
                            date: start ? formatDateShortLocalized(start, locale) : placeholder,
                        })}
                    </Alert>
                )}
                {state === 'active' && (
                    <>
                        {writesDisabled && (
                            <p id={demoNoteId} className={styles.note}>
                                {t('siteActions.detail.demoNote')}
                            </p>
                        )}
                        <div className={styles.actions}>
                            {ACTIONS.map((kind) =>
                                actionButton(
                                    kind,
                                    t(`siteActions.detail.actions.${kind}`),
                                    writesDisabled || needsStocking(kind)
                                        ? undefined
                                        : () => onAction(kind)
                                )
                            )}
                            {actionButton('harvest', t('siteActions.detail.actions.harvest'))}
                        </div>
                        {showsStockingNote && (
                            <p id={stockingNoteId} className={styles.note}>
                                {t('siteActions.detail.needsStocking')}
                            </p>
                        )}
                        <p id={harvestNoteId} className={styles.note}>
                            {t('siteActions.detail.harvestUnavailable')}
                        </p>
                    </>
                )}
            </section>
        </div>
    );

    const footer = dashboardPath ? (
        <Button variant="primary" to={dashboardPath} iconEnd={<Glyph icon={ArrowRightLine} />}>
            {t('siteActions.detail.viewDashboard')}
        </Button>
    ) : undefined;

    return (
        <ModalComponent
            isOpen
            size="lg"
            onClose={onClose}
            title={title}
            body={body}
            footer={footer}
        />
    );
}
